/**
 * useVisualViewportVars — publish the visual viewport as CSS variables while
 * `enabled`, so a position:fixed overlay can stay on the part of the screen the
 * user can actually see.
 *
 * Why: on phones the open chat is a fixed full-screen overlay (App.css). When the
 * on-screen keyboard opens, iOS Safari does not shrink the layout viewport that
 * fixed elements are positioned against — it shrinks and pans the *visual*
 * viewport to bring the focused input into view. The panel's header slid off the
 * top and its bottom sat under the keyboard. Sizing the overlay from these two
 * values keeps it exactly over the visible area, on iOS and Android alike.
 *
 *   --vv-height  visualViewport.height     (px)
 *   --vv-top     visualViewport.offsetTop  (px)
 *
 * Set on <html> and removed when disabled or unmounted; consumers fall back to
 * their old values via var(--vv-height, …). A no-op where visualViewport is
 * missing (old browsers, jsdom).
 */
import { useEffect } from 'react';

export function useVisualViewportVars(enabled: boolean): void {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!enabled || !vv) return;

    const root = document.documentElement;
    const update = () => {
      root.style.setProperty('--vv-height', `${vv.height}px`);
      root.style.setProperty('--vv-top', `${vv.offsetTop}px`);
    };

    update();
    vv.addEventListener('resize', update);
    // offsetTop changes on scroll (the pan), not on resize.
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      root.style.removeProperty('--vv-height');
      root.style.removeProperty('--vv-top');
    };
  }, [enabled]);
}
